/**
 * Question Presentation MCP Tool
 *
 * Wraps ask_user_question with safe question adaptation so the foreman otter
 * never has to construct the nested questions array itself — which causes
 * consistent serialization failures where the LLM passes the array as a
 * JSON string instead of a native array.
 *
 * The tool accepts manifest-format questions (what specialist otters produce),
 * adapts them via the shared question-adapter, and calls ask_user_question
 * with a guaranteed-correct native array.
 *
 * When `questions` is omitted, the tool reads from
 * .stackwright/question-manifest.json automatically by phase name.
 */

import { readFile, writeFile } from 'node:fs/promises';
import { existsSync, lstatSync, mkdirSync, renameSync } from 'node:fs';
import { join } from 'node:path';
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { adaptQuestions, type QuestionManifestQuestion } from '../question-adapter.js';
import { jsonCoerce } from '../coerce.js';
import { SAFE_PHASE, SAFE_QUESTION_ID } from '../validation.js';

const ManifestQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  type: z.enum(['text', 'select', 'multi-select', 'confirm']),
  required: z.boolean().optional(),
  options: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .optional(),
  default: z.union([z.string(), z.boolean(), z.array(z.string())]).optional(),
  help: z.string().optional(),
  dependsOn: z
    .object({
      questionId: z.string(),
      value: z.union([z.string(), z.array(z.string())]),
    })
    .optional(),
});

export function registerQuestionTools(server: McpServer): void {
  server.tool(
    'stackwright_pro_present_phase_questions',
    'Adapt manifest-format questions from a specialist otter and present them to the user via ask_user_question. ' +
      'Pass only the phase name — this tool reads questions from .stackwright/question-manifest.json automatically. ' +
      'The questions parameter is optional and only needed if the manifest has not been written yet. ' +
      'Use this instead of calling ask_user_question directly — it handles label truncation (50-char limit), ' +
      'header generation, confirm/text defaults, and correct array formatting automatically. ' +
      'IMPORTANT: This is the ONLY approved way to prepare questions before calling ask_user_question. ' +
      'Never call ask_user_question with raw manifest questions. ' +
      'Never retry ask_user_question validation errors by calling it directly — always re-call this tool.',
    {
      phase: z.string().describe('Phase name for display context, e.g. "designer", "api", "auth"'),
      questions: jsonCoerce(z.array(ManifestQuestionSchema).optional()).describe(
        'Questions in Question Manifest format. If omitted, questions are read ' +
          'from .stackwright/question-manifest.json using the phase name.'
      ),
      answers: jsonCoerce(
        z.record(z.string(), z.union([z.string(), z.array(z.string()), z.boolean()])).optional()
      ).describe('Previously collected answers used to resolve dependsOn conditions'),
    },
    async ({ phase, questions, answers }) => {
      // Resolution order:
      // 1. Use questions param if provided (explicit)
      // 2. Try per-phase file: .stackwright/questions/{phase}.json (new sink model)
      // 3. Fall back to monolithic manifest: .stackwright/question-manifest.json (legacy)
      let resolvedQuestions: QuestionManifestQuestion[];

      // Validate phase name — prevent path traversal (CWE-22)
      if (!SAFE_PHASE.test(phase)) {
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({
                error: `Invalid phase name: "${phase.slice(0, 50)}". Must be lowercase alphanumeric with hyphens, max 31 chars.`,
              }),
            },
          ],
          isError: true,
        };
      }

      if (questions && questions.length > 0) {
        resolvedQuestions = questions as QuestionManifestQuestion[];
      } else {
        // Try per-phase question file first (Sinks Not Pipes model)
        const phaseQuestionPath = join(process.cwd(), '.stackwright', 'questions', `${phase}.json`);
        try {
          const raw = await readFile(phaseQuestionPath, 'utf-8');
          const phaseData = JSON.parse(raw) as { questions: QuestionManifestQuestion[] };
          resolvedQuestions = (phaseData.questions ?? []) as QuestionManifestQuestion[];
        } catch {
          // Fall back to monolithic manifest (legacy path)
          try {
            const manifestPath = join(process.cwd(), '.stackwright', 'question-manifest.json');
            const raw = await readFile(manifestPath, 'utf-8');
            const manifest = JSON.parse(raw) as {
              phases: { phase: string; questions: QuestionManifestQuestion[] }[];
            };
            const phaseData = manifest.phases.find((p) => p.phase === phase);
            resolvedQuestions = (phaseData?.questions ?? []) as QuestionManifestQuestion[];
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            return {
              content: [
                {
                  type: 'text' as const,
                  text: JSON.stringify({
                    error: `Could not read question manifest for phase "${phase}": ${msg}`,
                    hint: 'Write the manifest first, or pass questions directly to this tool.',
                  }),
                },
              ],
              isError: true,
            };
          }
        }
      }

      // Adapt from manifest format to ask_user_question format
      const adapted = adaptQuestions(resolvedQuestions, answers ?? {});

      // Post-adaptation guard: validate all option labels are ≤50 chars.
      // adaptQuestions() truncates labels to 50 chars; this guard catches any
      // slip-through and surfaces a clear "50 characters" message instead of
      // the misleading "50 items" message that ask_user_question would produce.
      const labelSchema = z.string().max(50, 'Value should have at most 50 characters');
      for (const q of adapted) {
        for (const opt of q.options) {
          const check = labelSchema.safeParse(opt.label);
          if (!check.success) {
            return {
              content: [
                {
                  type: 'text' as const,
                  text: JSON.stringify({
                    error: `Option label for phase "${phase}" exceeds 50 characters: ${check.error.issues[0]?.message ?? 'label too long'}. Truncate option labels to ≤50 characters before calling this tool.`,
                  }),
                },
              ],
              isError: true,
            };
          }
        }
      }

      if (adapted.length === 0) {
        return {
          content: [
            {
              type: 'text' as const,
              text:
                `Phase "${phase}" has no questions to present. ` +
                `Do NOT call ask_user_question. ` +
                `Call stackwright_pro_save_phase_answers({ phase: "${phase}", rawAnswers: [] }) directly, then proceed to the execution step for this phase.`,
            },
            {
              type: 'text' as const,
              // Empty array — second block always present so the foreman's two-block contract holds
              text: JSON.stringify([]),
            },
          ],
          isError: false,
        };
      }

      // Return the adapted questions for the foreman to pass to ask_user_question.
      // Two-block format: first block is the instruction, second block is the raw
      // JSON array. Splitting them removes the temptation for the LLM to
      // re-serialise the array when it reads the instruction text.
      return {
        content: [
          {
            type: 'text' as const,
            text:
              `Adapted ${adapted.length} questions for phase "${phase}". ` +
              `Pass the JSON array below DIRECTLY to ask_user_question as the "questions" parameter. ` +
              `Do NOT JSON.stringify() it. Do NOT wrap it in an object. Pass the parsed array value as-is.`,
          },
          {
            type: 'text' as const,
            text: JSON.stringify(adapted),
          },
        ],
        isError: false,
      };
    }
  );

  // ---------------------------------------------------------------------------
  // stackwright_pro_get_next_question
  // ---------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_get_next_question',
    'Returns the next unanswered question for a phase as a plain JSON object — one question at a time. ' +
      'Returns { done: true } when all questions are answered or the phase has no questions. ' +
      'Call this in a loop: present the question in plain chat, get user reply, call record_answer, repeat.',
    { phase: z.string().describe('Phase name e.g. "designer", "api", "auth"') },
    async ({ phase }) => {
      if (!SAFE_PHASE.test(phase)) {
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({ error: `Invalid phase name: "${phase.slice(0, 50)}"` }),
            },
          ],
          isError: true,
        };
      }

      const cwd = process.cwd();

      // Read questions file
      const questionsPath = join(cwd, '.stackwright', 'questions', `${phase}.json`);
      let questions: Array<{
        id: string;
        question: string;
        type: string;
        options?: Array<{ label: string; value: string }>;
        help?: string;
      }> = [];
      try {
        const raw = await readFile(questionsPath, 'utf-8');
        const parsed = JSON.parse(raw) as { questions?: typeof questions };
        questions = parsed.questions ?? [];
      } catch {
        // File missing or malformed → phase has no questions
        return { content: [{ type: 'text' as const, text: JSON.stringify({ done: true }) }] };
      }

      if (questions.length === 0) {
        return { content: [{ type: 'text' as const, text: JSON.stringify({ done: true }) }] };
      }

      // Read existing answers to build the answered-ID set
      const answersPath = join(cwd, '.stackwright', 'answers', `${phase}.json`);
      let answeredIds = new Set<string>();
      try {
        const raw = await readFile(answersPath, 'utf-8');
        const parsed = JSON.parse(raw) as { answers?: Record<string, unknown> };
        answeredIds = new Set(Object.keys(parsed.answers ?? {}));
      } catch {
        // File missing → nothing answered yet
      }

      // First unanswered question
      const next = questions.find((q) => !answeredIds.has(q.id));
      if (!next) {
        return { content: [{ type: 'text' as const, text: JSON.stringify({ done: true }) }] };
      }

      return {
        content: [
          {
            type: 'text' as const,
            text: JSON.stringify({
              done: false,
              questionId: next.id,
              question: next.question,
              type: next.type,
              options: next.options ?? null,
              help: next.help ?? null,
              index: questions.indexOf(next) + 1,
              total: questions.length,
            }),
          },
        ],
      };
    }
  );

  // ---------------------------------------------------------------------------
  // stackwright_pro_record_answer
  // ---------------------------------------------------------------------------
  server.tool(
    'stackwright_pro_record_answer',
    'Records a single answer to a phase question. Appends to .stackwright/answers/{phase}.json incrementally. ' +
      'Idempotent — calling twice for the same questionId overwrites. ' +
      'Call after receiving each user reply in the conversational question loop.',
    {
      phase: z.string().describe('Phase name e.g. "designer"'),
      questionId: z.string().describe('The question ID from get_next_question, e.g. "designer-1"'),
      answer: z.string().describe("The user's free-text answer"),
    },
    async ({ phase, questionId, answer }) => {
      if (!SAFE_PHASE.test(phase)) {
        return {
          content: [
            { type: 'text' as const, text: JSON.stringify({ error: 'Invalid phase name' }) },
          ],
          isError: true,
        };
      }
      if (!SAFE_QUESTION_ID.test(questionId)) {
        return {
          content: [
            { type: 'text' as const, text: JSON.stringify({ error: 'Invalid questionId' }) },
          ],
          isError: true,
        };
      }
      const safeAnswer = answer.slice(0, 2000);

      const cwd = process.cwd();
      const answersDir = join(cwd, '.stackwright', 'answers');
      const answersPath = join(answersDir, `${phase}.json`);

      // Symlink guard on directory
      if (existsSync(answersDir) && lstatSync(answersDir).isSymbolicLink()) {
        return {
          content: [
            {
              type: 'text' as const,
              text: JSON.stringify({ error: 'answers dir is a symlink — refusing to write' }),
            },
          ],
          isError: true,
        };
      }
      mkdirSync(answersDir, { recursive: true });

      // Read existing or start fresh
      let existing: {
        version: string;
        phase: string;
        answers: Record<string, string>;
        updatedAt?: string;
      } = {
        version: '1.0',
        phase,
        answers: {},
      };
      try {
        if (existsSync(answersPath)) {
          if (lstatSync(answersPath).isSymbolicLink()) {
            return {
              content: [
                {
                  type: 'text' as const,
                  text: JSON.stringify({ error: 'answers file is a symlink — refusing to write' }),
                },
              ],
              isError: true,
            };
          }
          const raw = await readFile(answersPath, 'utf-8');
          existing = JSON.parse(raw) as typeof existing;
        }
      } catch {
        // Malformed → start fresh
      }

      // Upsert
      existing.answers[questionId] = safeAnswer;
      existing.updatedAt = new Date().toISOString();

      // Atomic write via tmp+rename
      const tmp = `${answersPath}.tmp`;
      await writeFile(tmp, JSON.stringify(existing, null, 2), 'utf-8');
      renameSync(tmp, answersPath);

      return {
        content: [
          { type: 'text' as const, text: JSON.stringify({ recorded: true, phase, questionId }) },
        ],
      };
    }
  );
}
